/*
 * @providesModule PushButton
 *
 */

import React from 'react';
import PropTypes from 'prop-types';

import _ from 'lodash';

import {Text, View, StyleSheet, Platform, TouchableOpacity, ActivityIndicator} from 'react-native';

import Label from 'Label';
import LinearGradient from 'react-native-linear-gradient';
import SkillzStyle, {lightenColor, darkenColor} from 'SkillzStyle';
import SkillzImage from 'SkillzImage';

export default class PushButton extends React.Component {
  static propTypes = {
    accessibilityLabel: PropTypes.string,
    testID: PropTypes.string,
    cornerRadius: PropTypes.number,
    colors: PropTypes.arrayOf(PropTypes.any),
    title: PropTypes.string,
    textStyle: PropTypes.any,
    textContainer: PropTypes.any,
    style: PropTypes.any,
    onPress: PropTypes.func,
    numberOfLines: PropTypes.number,
    icon: PropTypes.string,
    iconStyle: PropTypes.any,
    resizeMode: PropTypes.string,
    enabled: PropTypes.bool,
    iconColor: PropTypes.string,
    children: PropTypes.node,
    hitSlop: PropTypes.object,
    disabled: PropTypes.bool,
    pulseIcon: PropTypes.bool,
    pulseLengthMs: PropTypes.number,
    pulseScale: PropTypes.number,
    isLoading: PropTypes.bool,
  };

  static defaultProps = {
    title: '',
    onPress: () => {
      /* */
    },
    cornerRadius: 0,
    colors: ['gray'],
    numberOfLines: 1,
    enabled: true,
    pulseIcon: false,
    pulseLengthMs: 500,
    pulseScale: 1.075,
    onLayout: () => {
      /* */
    },
    isLoading: false,
  };

  static allowTouches = true;

  state: {
    width: number;
    height: number;
  };

  props: {
    numberOfLines: number;
    title: string;
    cornerRadius: number;
    colors: Array<any>;
  };

  constructor(props: any) {
    super(props);

    this.hasPushed = false;
    this.state = {width: 0, height: 0};
  }

  onLayout(e: {nativeEvent: {layout: {width: number; height: number}}}) {
    this.setState({width: e.nativeEvent.layout.width, height: e.nativeEvent.layout.height});
  }

  _renderIcon(): React.ReactNode | null {
    const iconStyle = this.props.iconStyle || {};
    if (this.props.icon) {
      if (this.props.iconColor) {
        return (
          <View style={[{height: 32, width: 32}, iconStyle]}>
            <SkillzImage
              style={{height: 32, width: 32, position: 'absolute'}}
              resizeMode={this.props.resizeMode || 'contain'}
              source={this.props.icon}
              pulseIcon={this.props.pulseIcon}
            />
            <SkillzImage
              style={{height: 32, width: 32, position: 'absolute'}}
              resizeMode={this.props.resizeMode || 'contain'}
              color={this.props.iconColor}
              source={this.props.icon}
              pulseIcon={this.props.pulseIcon}
            />
          </View>
        );
      }
      return (
        <SkillzImage
          style={[{height: 32, width: 32}, iconStyle]}
          resizeMode={this.props.resizeMode || 'contain'}
          source={this.props.icon}
          pulseIcon={this.props.pulseIcon}
        />
      );
    }
    return null;
  }

  componentDidUpdate() {
    this.hasPushed = false;
  }

  doPush = () => {
    if (Platform.OS == 'android') {
      if (!this.props.enabled || !PushButton.allowTouches) {
        return;
      }
      PushButton.allowTouches = false;
      setTimeout(() => {
        PushButton.allowTouches = true;
      }, 300); // eslint-disable-line no-magic-numbers
    } else {
      if (!this.props.enabled) {
        return;
      }
    }
    this.props.onPress();
  };

  _renderText(): React.ReactNode | null {
    const textStyle = StyleSheet.flatten(this.props.textStyle) || {};
    if (this.props.isLoading) {
      return (
        <View key='innerText' style={{flexDirection: 'column', flex: 1, alignItems: 'stretch'}}>
          <ActivityIndicator
            size='small'
            color={textStyle.color ?? SkillzStyle.fullStyles.textColorPrimary.color}
          />
        </View>
      );
    }
    if (this.props.title) {
      return (
        <View key='innerText' style={{flexDirection: 'column', flex: 1, alignItems: 'stretch'}}>
          <Label
            numberOfLines={this.props.numberOfLines}
            style={[{marginHorizontal: 10, textAlign: 'center'}, textStyle]}
          >
            {this.props.title}
          </Label>
        </View>
      );
    } else {
      return false;
    }
  }

  _renderInnerContent(): React.ReactNode | null {
    const radius = this.props.cornerRadius;

    if (this.props.children) {
      return this.props.children;
    } else {
      const textContainer = StyleSheet.flatten(this.props.textContainer) || {};
      return (
        <View
          key={this.props.title}
          pointerEvents={'none'}
          style={[
            {borderRadius: radius, alignItems: 'stretch'},
            styles.textContainer,
            textContainer,
          ]}
        >
          <View key={'interior'}>
            <View
              key={'interior2'}
              style={{
                flexGrow: 1,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <View key='icon'>{this._renderIcon()}</View>
              {this._renderText()}
            </View>
          </View>
        </View>
      );
    }
  }

  render(): React.ReactNode {
    let colors = this.props.colors;

    if (colors.length == 1) {
      colors = [colors[0], colors[0]];
    }
    let radius = this.props.cornerRadius;
    const lightColor = lightenColor(this.props.colors[0]);
    const darkColor = darkenColor(this.props.colors[this.props.colors.length - 1]);
    let style = StyleSheet.flatten(this.props.style) || {};

    style = _.omit(style, ['justifyContent', 'alignItems']);

    const stops = [0, 1];
    const highlightColors = [lightColor, darkColor];
    const opacity = this.props.enabled ? 1.0 : 0.3; // eslint-disable-line no-magic-numbers
    const activeOpacity = this.props.enabled ? 0.25 : 1.0; // eslint-disable-line no-magic-numbers
    const hitSlop = this.props.hitSlop || {top: 0, bottom: 0, left: 0, right: 0};

    const height = Platform.select({ios: 40, android: 42});
    radius = Platform.select({android: radius, ios: radius + 2, web: radius});

    return (
      <View
        key={this.props.title}
        onLayout={this.props.onLayout}
        style={[{height, alignSelf: 'stretch'}, style, {borderRadius: radius - 1}]}
      >
        <View key='background' style={{flex: 1}}>
          <TouchableOpacity
            activeOpacity={activeOpacity}
            onPress={this.doPush}
            style={{flex: 1}}
            hitSlop={hitSlop}
            disabled={this.props.disabled}
            accessibilityRole={'button'}
            accessible={true}
            accessibilityLabel={this.props.accessibilityLabel}
            testID={this.props.testID}
          >
            <View style={{flexGrow: 1, opacity}}>
              <LinearGradient
                pointerEvents={'none'}
                colors={highlightColors}
                stops={stops}
                style={[StyleSheet.absoluteFill, {flexGrow: 1, borderRadius: radius - 1}]}
              ></LinearGradient>
              <LinearGradient
                colors={colors}
                style={{
                  borderRadius: radius - 2,
                  position: 'absolute',
                  left: 0,
                  top: 1,
                  right: 0,
                  bottom: 1,
                }}
              ></LinearGradient>
              {this._renderInnerContent()}
            </View>
          </TouchableOpacity>
        </View>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  upperHighlight: {
    position: 'absolute',
    flexGrow: 1,
    marginBottom: 1,
    marginLeft: 0,
    marginRight: 0,
    borderWidth: 1,
  },
  lowerHighligh: {
    position: 'absolute',
    flexGrow: 1,
    marginBottom: 1,
    borderWidth: 1,
  },
  buttonContainer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    flexGrow: 1,
  },
  button: {
    position: 'absolute',
    top: 1,
    left: 0,
    right: 0,
    bottom: 1,
  },
  highlightContainer: {
    flexDirection: 'column',
    position: 'absolute',
    flexGrow: 1,
    left: 0,
    right: 0,
    bottom: 0,
    top: 0,
    justifyContent: 'space-between',
  },
  gradientFill: {
    flexGrow: 1,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    marginTop: 1,
    marginLeft: 0,
    marginRight: 0,
    marginBottom: 1,
    position: 'absolute',
  },
  buttonFill: {
    flexGrow: 1,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    marginTop: 1,
    marginLeft: 0,
    marginRight: 0,
    marginBottom: 1,
    position: 'absolute',
    borderRightWidth: 1,
    borderLeftWidth: 1,
  },
  textContainer: {
    ...Platform.select({
      native: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        justifyContent: 'center',
        alignItems: 'stretch',
      },
      web: {
        flex: 1,
        paddingVertical: 4,
        justifyContent: 'center',
        alignItems: 'stretch',
      },
    }),
  },
});
